/**
 * Prices Section - Price breakdown grid for different finishes
 *
 * Shared component for displaying card prices.
 * Used by collection, deck editor, and other card details panels.
 * Accepts either ScryfallCard directly or EnrichedCollectionCard.
 */
import type { ReactElement } from 'react';

import useUserSettings from '@/features/settings/hooks/api';
import { formatPrice } from '@/lib/price';
import type { EnrichedCollectionCard, Finish, ScryfallCard } from '@/types/card';

export interface PricesSectionProps {
  /** Card to display prices for - accepts either EnrichedCollectionCard or ScryfallCard */
  card: EnrichedCollectionCard | ScryfallCard;
  /** Optional finish to highlight (defaults to 'nonfoil' for ScryfallCard input) */
  finish?: Finish;
}

interface PriceItem {
  label: string;
  value: string | null;
  isActive: boolean;
}

/** Type guard to check if card is EnrichedCollectionCard */
function isEnrichedCard(card: EnrichedCollectionCard | ScryfallCard): card is EnrichedCollectionCard {
  return 'scryfall_id' in card && 'card' in card;
}

/** Parse string price value and format with currency */
function formatStringPrice(value: string | null, currency: 'USD' | 'EUR'): string {
  if (!value) return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return formatPrice(num, currency);
}

export default function PricesSection({ card, finish: finishOverride }: PricesSectionProps): ReactElement {
  const { settings } = useUserSettings();

  // Extract prices and finish from either input type
  const scryfallCard = isEnrichedCard(card) ? card.card : card;
  const prices = scryfallCard.prices;
  const finish = finishOverride ?? (isEnrichedCard(card) ? card.finish : 'nonfoil');
  const isEur = settings.currency === 'EUR';

  // Build price items based on currency preference
  const priceItems: PriceItem[] = isEur
    ? [
        { label: 'EUR', value: prices.eur, isActive: finish === 'nonfoil' },
        { label: 'EUR Foil', value: prices.eur_foil, isActive: finish === 'foil' },
        { label: 'EUR Etched', value: prices.eur_etched, isActive: finish === 'etched' },
      ]
    : [
        { label: 'USD', value: prices.usd, isActive: finish === 'nonfoil' },
        { label: 'USD Foil', value: prices.usd_foil, isActive: finish === 'foil' },
        { label: 'USD Etched', value: prices.usd_etched, isActive: finish === 'etched' },
      ];

  // Filter out items with no value and no active state
  const displayItems = priceItems.filter((item) => item.value !== null);

  if (displayItems.length === 0) {
    return <></>;
  }

  return (
    <section className="px-6 pb-6">
      <div className="bg-slate/50 border-storm rounded-xl border p-5">
        {/* Section Header */}
        <h3 className="text-ash mb-4 text-xs font-medium tracking-widest uppercase">Prices</h3>

        {/* Price Grid */}
        <div className="grid grid-cols-3 gap-4">
          {priceItems.map((item) => (
            <div key={item.label} className="text-center">
              <p className="text-ash mb-1 text-xs">{item.label}</p>
              <p
                className={`font-mono text-base ${
                  item.isActive ? 'text-pearl font-medium' : 'text-silver'
                } ${item.isActive ? 'border-aether border-b-2' : ''}`}
              >
                {formatStringPrice(item.value, settings.currency)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
